import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
  Dialog,
  DialogContent,
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import PrintRounded from "@mui/icons-material/PrintRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ZoomInRounded from "@mui/icons-material/ZoomInRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import { LoadingButton } from "@mui/lab";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import jsPDF from "jspdf";

import { DefaultPage } from "../../layouts/default-page";
import { StatusChip } from "../../components/common/StatusChip";
import { Notify } from "../../layouts/notify";
import ImageFunctions from "../../components/Functions/PdfImageFunctions";
import { User } from "../../models/User";
import {
  useGetPublicReportByIdQuery,
  useUpdatePublicReportStatusMutation,
  useAddPublicResponseMutation,
} from "../../services/report.query";

export default function PublicReportDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  // Support route param :id, query param ?id=, and navigation state.report
  const stateReport = location.state?.report;
  const reportId = params.id || searchParams.get("id") || (stateReport?.id ? String(stateReport.id) : undefined);

  const {
    data: fetchedReport,
    isLoading: fetchingReport,
    isError,
    refetch,
  } = useGetPublicReportByIdQuery(reportId as any, {
    skip: !reportId,
  });

  const report = fetchedReport || stateReport;

  const lUser: User = JSON.parse(localStorage.getItem("user") || "{}");
  const [selectedStatus, setSelectedStatus] = useState<string>("PENDING");
  const [adminResponse, setAdminResponse] = useState<string>("");
  const [printLoading, setPrintLoading] = useState(false);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  const [updateStatus, { isLoading: updatingStatus }] =
    useUpdatePublicReportStatusMutation();
  const [addResponse, { isLoading: sendingResponse }] =
    useAddPublicResponseMutation();

  const handleBack = () => {
    if (location.state?.returnUrl) {
      navigate(location.state.returnUrl);
    } else {
      const saved = sessionStorage.getItem("last_public_reports_url");
      if (saved && saved.startsWith("/public-reports")) {
        navigate(saved);
      } else {
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    if (report?.status) {
      setSelectedStatus(report.status.toUpperCase());
    }
  }, [report]);

  const handleUpdateStatus = async () => {
    if (!report?.id) return;
    try {
      await updateStatus({
        id: report.id,
        status: selectedStatus,
        user_id: lUser.user_id,
      }).unwrap();

      if (adminResponse.trim()) {
        await addResponse({
          report_id: report.id,
          response: adminResponse.trim(),
        }).unwrap();
        setAdminResponse("");
      }

      Notify("success", "Public report status updated successfully.");
      refetch();
    } catch (err: any) {
      console.error("Status update error:", err);
      Notify("error", err?.message || "Failed to update report status.");
    }
  };

  const handlePrintPDF = async () => {
    if (!report) return;
    setPrintLoading(true);
    const doc = new jsPDF();
    const imageLoader = new ImageFunctions();

    try {
      const bannerImageBase64 = await imageLoader.loadLocalImageAsBase64(
        `${process.env.PUBLIC_URL}/pdf-banner.jpg`
      );

      doc.addImage(bannerImageBase64, "PNG", 14, 8, 182, 35);

      doc.setFontSize(18);
      doc.setTextColor(40, 40, 40);
      doc.text("Citizen Public Report", 14, 50);

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text("Report Reference ID:", 14, 56);
      doc.setTextColor(0, 0, 0);
      doc.text(String(report.id), 70, 56);

      doc.setTextColor(100, 100, 100);
      doc.text("Report Date & Time:", 14, 62);
      doc.setTextColor(0, 0, 0);
      doc.text(
        report.created_at
          ? format(new Date(report.created_at), "MMMM dd, yyyy · hh:mm a")
          : "-",
        70,
        62
      );

      doc.setFontSize(12);
      doc.setTextColor(0, 114, 188);
      doc.text("Citizen Information:", 14, 76);

      const userInfo = [
        {
          label: "Full Name:",
          value: `${report.user?.name || ""} ${report.user?.last_name || ""}`,
        },
        { label: "Email Address:", value: report.user?.email || "-" },
        { label: "Phone Number:", value: report.user?.phone_number || "-" },
        { label: "User Role:", value: report.user?.role || "CITIZEN" },
      ];

      let currentY = 82;
      const lineHeight = 6;
      userInfo.forEach((info) => {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(info.label, 14, currentY);
        doc.setTextColor(0, 0, 0);
        doc.text(info.value, 60, currentY);
        currentY += lineHeight;
      });

      currentY += 4;
      doc.setFontSize(12);
      doc.setTextColor(0, 114, 188);
      doc.text("Content & Exhibition Details:", 14, currentY);
      currentY += 6;

      const details = [
        {
          label: "Motion Content Title:",
          value: report.title || "Unspecified",
        },
        {
          label: "Exhibition Venue / TV Channel:",
          value: report.name || "Unspecified",
        },
        {
          label: "Exhibition Date & Time:",
          value: report.event_datetime
            ? format(new Date(report.event_datetime), "MMMM dd, yyyy · hh:mm a")
            : "-",
        },
        {
          label: "Current Status:",
          value: report.status || "PENDING",
        },
      ];

      details.forEach((d) => {
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(d.label, 14, currentY);
        doc.setTextColor(0, 0, 0);
        doc.text(d.value, 75, currentY);
        currentY += lineHeight;
      });

      currentY += 4;
      doc.setFontSize(12);
      doc.setTextColor(0, 114, 188);
      doc.text("Description of Scenes Complained About:", 14, currentY);
      currentY += 6;

      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      const splitDescription = doc.splitTextToSize(
        report.report_description || "No description provided.",
        180
      );
      doc.text(splitDescription, 14, currentY);

      doc.save(`Public_Report_${report.id}.pdf`);
      Notify("success", "Public report PDF generated successfully.");
    } catch (e) {
      console.error(e);
      Notify("error", "Failed to generate report PDF.");
    } finally {
      setPrintLoading(false);
    }
  };

  if (fetchingReport && !report) {
    return (
      <DefaultPage title="Public Report Details" subTitle="Loading report details...">
        <Box sx={{ p: 4, width: "100%" }}>
          <Skeleton variant="rectangular" height={50} sx={{ borderRadius: "6px", mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: "6px", mb: 2 }} />
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: "6px" }} />
            </Grid>
            <Grid item xs={12} md={5}>
              <Skeleton variant="rectangular" height={320} sx={{ borderRadius: "6px" }} />
            </Grid>
          </Grid>
        </Box>
      </DefaultPage>
    );
  }

  if (!fetchingReport && !report) {
    return (
      <DefaultPage title="Public Report" subTitle="Report not found">
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error" variant="h6" mb={2}>
            {isError ? "Failed to load public report details." : "Public report not found or was removed."}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackRounded />}
            onClick={handleBack}
            sx={{ backgroundColor: "#0072BC" }}
          >
            Back to Public Reports
          </Button>
        </Box>
      </DefaultPage>
    );
  }

  const user = report.user;
  const attachments = report.attachments || [];
  const responses = report.responses || [];

  return (
    <DefaultPage
      title="Public Report Details"
      subTitle="Review, evaluate, and moderate content issue reports submitted by general citizens."
    >
      {/* Top Navigation & Action Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackRounded fontSize="small" />}
          onClick={handleBack}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "4px",
            borderColor: "#CBD5E1",
            color: "#475569",
            "&:hover": {
              borderColor: "#94A3B8",
              backgroundColor: "#F8FAFC",
            },
          }}
        >
          Back to Public Reports
        </Button>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <StatusChip label={report.status || "PENDING"} />
          <Button
            variant="contained"
            size="small"
            startIcon={<PrintRounded fontSize="small" />}
            onClick={handlePrintPDF}
            disabled={printLoading}
            sx={{
              textTransform: "none",
              fontWeight: 650,
              borderRadius: "4px",
              backgroundColor: "#0072BC",
              "&:hover": { backgroundColor: "#005a96" },
              boxShadow: "none",
            }}
          >
            {printLoading ? "Generating..." : "Print PDF"}
          </Button>
        </Stack>
      </Box>

      {/* Main Grid Content */}
      <Grid container spacing={3}>
        {/* Left Column: Details & Evidence */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={3}>
            {/* Citizen Information Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Citizen Information
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
                  <Box
                    component="img"
                    src={
                      user?.profile_picture ||
                      "https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/assets/default-user-image.png"
                    }
                    alt="Citizen Avatar"
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid #E2E8F0",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      window.open(user?.profile_picture, "_blank")
                    }
                  />
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A" }}>
                      {user?.name || "Anonymous"} {user?.last_name || ""}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                      Citizen Submitter · {user?.role || "USER"}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Email Address
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#1E293B", fontWeight: 500 }}>
                      {user?.email || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Contact Number
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#1E293B", fontWeight: 500 }}>
                      {user?.phone_number || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Date & Time Reported
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#1E293B", fontWeight: 500 }}>
                      {report.created_at
                        ? format(new Date(report.created_at), "MMM dd, yyyy · hh:mm a")
                        : "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Report Reference ID
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        color: "#0072BC",
                        fontWeight: 700,
                        fontFamily: "monospace",
                      }}
                    >
                      #{report.id}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Content & Incident Details Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Content & Exhibition Details
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Motion Content Title
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0F172A" }}>
                      {report.title || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Exhibition Venue / TV Network
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#1E293B" }}>
                      {report.name || "—"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600 }}>
                      Date & Time of Exhibition
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: "#1E293B", fontWeight: 500 }}>
                      {report.event_datetime
                        ? format(new Date(report.event_datetime), "MMMM dd, yyyy · hh:mm a")
                        : "—"}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 2, borderColor: "#F1F5F9" }} />

                <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600, mb: 1 }}>
                  Details of Scenes Complained About
                </Typography>
                <Box
                  sx={{
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    borderRadius: "4px",
                    p: 2,
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: "#1E293B",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {report.report_description || "No description provided."}
                </Box>
              </CardContent>
            </Card>

            {/* Media Attachments & Evidence Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Media Attachments & Evidence ({attachments.length})
                </Typography>

                {attachments.length === 0 ? (
                  <Box
                    sx={{
                      p: 3,
                      textAlign: "center",
                      backgroundColor: "#F8FAFC",
                      borderRadius: "4px",
                      border: "1px dashed #CBD5E1",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#64748B" }}>
                      No photo or video attachments were uploaded with this report.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {attachments.map((att: any, idx: number) => {
                      const isVideo = att.type === "VIDEO" || att.file_url?.endsWith(".mp4");
                      return (
                        <Grid item xs={12} sm={6} md={isVideo ? 12 : 6} key={att.id || idx}>
                          <Box
                            sx={{
                              border: "1px solid #E2E8F0",
                              borderRadius: "4px",
                              overflow: "hidden",
                              backgroundColor: "#F8FAFC",
                            }}
                          >
                            {isVideo ? (
                              <Box sx={{ p: 1 }}>
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#475569",
                                    mb: 1,
                                    px: 0.5,
                                  }}
                                >
                                  Video Recording
                                </Typography>
                                <CardMedia
                                  component="video"
                                  controls
                                  src={att.file_url}
                                  sx={{
                                    width: "100%",
                                    maxHeight: 280,
                                    borderRadius: "4px",
                                    backgroundColor: "#000",
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  position: "relative",
                                  height: 180,
                                  cursor: "pointer",
                                  "&:hover .zoom-overlay": {
                                    opacity: 1,
                                  },
                                }}
                                onClick={() => setZoomedImage(att.file_url)}
                              >
                                <CardMedia
                                  component="img"
                                  image={att.file_url}
                                  alt="Attachment"
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                                <Box
                                  className="zoom-overlay"
                                  sx={{
                                    position: "absolute",
                                    inset: 0,
                                    backgroundColor: "rgba(0, 0, 0, 0.45)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    opacity: 0,
                                    transition: "opacity 0.2s ease",
                                    color: "#FFFFFF",
                                    gap: 0.5,
                                    fontSize: 13,
                                    fontWeight: 600,
                                  }}
                                >
                                  <ZoomInRounded />
                                  <span>Click to Zoom</span>
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right Column: Resolution & Status Management */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            {/* Status & Resolution Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Resolution & Status
                </Typography>

                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600, mb: 1 }}>
                    Change Report Status
                  </Typography>
                  <FormControl fullWidth size="small">
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                      labelId="status-select-label"
                      label="Status"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      sx={{ borderRadius: "4px", fontSize: 13.5 }}
                    >
                      <MenuItem value="PENDING">PENDING (Under Evaluation)</MenuItem>
                      <MenuItem value="ACKNOWLEDGED">ACKNOWLEDGED (Official Action Taken)</MenuItem>
                      <MenuItem value="DUPLICATE">DUPLICATE (Repeated Report)</MenuItem>
                      <MenuItem value="REJECTED">REJECTED (Insufficient Basis)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600, mb: 1 }}>
                    Official Administrative Response / Remarks
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    size="small"
                    placeholder="Enter official resolution notes or comments to be communicated to the reporting citizen..."
                    value={adminResponse}
                    onChange={(e) => setAdminResponse(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        fontSize: 13.5,
                      },
                    }}
                  />
                  <Typography variant="caption" sx={{ color: "#94A3B8", mt: 0.5, display: "block" }}>
                    This response will be visible to the citizen submitter via the MTRCB Mobile App.
                  </Typography>
                </Box>

                <LoadingButton
                  fullWidth
                  variant="contained"
                  loading={updatingStatus || sendingResponse}
                  startIcon={<SendRounded fontSize="small" />}
                  onClick={handleUpdateStatus}
                  sx={{
                    backgroundColor: "#0072BC",
                    "&:hover": { backgroundColor: "#005a96" },
                    borderRadius: "4px",
                    textTransform: "none",
                    fontWeight: 650,
                    fontSize: 13.5,
                    py: 1,
                    boxShadow: "none",
                  }}
                >
                  Update Status & Send Response
                </LoadingButton>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PrintRounded fontSize="small" />}
                  onClick={handlePrintPDF}
                  disabled={printLoading}
                  sx={{
                    mt: 1.5,
                    borderRadius: "4px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13.5,
                    py: 0.9,
                    borderColor: "#CBD5E1",
                    color: "#334155",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  {printLoading ? "Generating PDF..." : "Print PDF Report"}
                </Button>

                {report.admin && (
                  <Box
                    sx={{
                      mt: 2.5,
                      p: 1.5,
                      backgroundColor: "#F8FAFC",
                      border: "1px solid #E2E8F0",
                      borderRadius: "4px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                    }}
                  >
                    <CheckCircleOutlineRounded sx={{ color: "#0072BC", fontSize: 18 }} />
                    <Typography sx={{ fontSize: 12.5, color: "#475569" }}>
                      Last updated by:{" "}
                      <strong>
                        {report.admin.name} {report.admin.last_name}
                      </strong>
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Response History Card */}
            {responses.length > 0 && (
              <Card
                elevation={0}
                sx={{
                  border: "1px solid #E2E8F0",
                  borderRadius: "6px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#0072BC",
                      mb: 2,
                    }}
                  >
                    Response History ({responses.length})
                  </Typography>

                  <Stack spacing={2}>
                    {responses.map((resp: any, i: number) => (
                      <Box
                        key={resp.id || i}
                        sx={{
                          p: 1.75,
                          backgroundColor: "#F8FAFC",
                          border: "1px solid #E2E8F0",
                          borderRadius: "4px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 0.75,
                          }}
                        >
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>
                            {resp.admin
                              ? `${resp.admin.name} ${resp.admin.last_name}`
                              : "MTRCB Board Administrator"}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#64748B" }}>
                            {resp.created_at
                              ? format(new Date(resp.created_at), "MMM dd, yyyy · hh:mm a")
                              : ""}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>
                          {resp.response}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Fullscreen Image Lightbox Modal */}
      <Dialog
        open={Boolean(zoomedImage)}
        onClose={() => setZoomedImage(null)}
        maxWidth="md"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={() => setZoomedImage(null)}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.85)" },
            }}
          >
            <CloseRounded />
          </IconButton>
          {zoomedImage && (
            <Box
              component="img"
              src={zoomedImage}
              alt="Enlarged attachment"
              sx={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                borderRadius: "6px",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </DefaultPage>
  );
}
